import { parseFormulaExpression } from './formula-parser';
import { CADENCE_RANK } from './types';
import type { AggregateFunction, Cadence, FormulaExpression, Metric } from './types';

export type CatalogDefinition =
  | { origin: 'input' }
  | { origin: 'formula'; formula: string }
  | { origin: 'aggregate'; source: string; function: AggregateFunction; trailingPeriods?: number };

export type CatalogGroup = 'fundamentals' | 'subscription' | 'one_time_sales';

export interface CatalogEntry {
  key: string;
  title: string;
  cadence: Cadence;
  group: CatalogGroup;
  unit?: '$' | '%';
  decimals?: number;
  definition: CatalogDefinition;
}

export type BusinessModel = 'subscription' | 'one_time_sales' | 'services';

const input = { origin: 'input' } as const;
const formula = (source: string): CatalogDefinition => ({ origin: 'formula', formula: source });
const aggregate = (source: string, fn: AggregateFunction, trailingPeriods?: number): CatalogDefinition => ({
  origin: 'aggregate', source, function: fn, ...(trailingPeriods ? { trailingPeriods } : {}),
});

const fundamentals: Omit<CatalogEntry, 'group'>[] = [
  { key: 'cash_on_hand', title: 'Cash on hand', cadence: 'weekly', unit: '$', definition: input },
  { key: 'net_cash_change', title: 'Net cash change', cadence: 'weekly', unit: '$', definition: formula('{cash_on_hand} - PREV({cash_on_hand})') },
  { key: 'revenue', title: 'Revenue', cadence: 'monthly', unit: '$', definition: input },
  { key: 'cost_of_goods_sold', title: 'Cost of goods sold', cadence: 'monthly', unit: '$', definition: input },
  { key: 'operating_expenses', title: 'Operating expenses', cadence: 'monthly', unit: '$', definition: input },
  { key: 'net_income', title: 'Net income', cadence: 'monthly', unit: '$', definition: input },
  { key: 'headcount', title: 'Headcount (FTE)', cadence: 'monthly', decimals: 1, definition: input },
  { key: 'gross_profit', title: 'Gross profit', cadence: 'monthly', unit: '$', definition: formula('{revenue} - {cost_of_goods_sold}') },
  { key: 'gross_margin', title: 'Gross margin', cadence: 'monthly', unit: '%', definition: formula('{gross_profit} / {revenue}') },
  { key: 'ebitda', title: 'EBITDA', cadence: 'monthly', unit: '$', definition: formula('{gross_profit} - {operating_expenses}') },
  { key: 'ebitda_margin', title: 'EBITDA margin', cadence: 'monthly', unit: '%', definition: formula('{ebitda} / {revenue}') },
  { key: 'net_margin', title: 'Net margin', cadence: 'monthly', unit: '%', definition: formula('{net_income} / {revenue}') },
  { key: 'cash_on_hand_month_end', title: 'Cash on hand (month-end)', cadence: 'monthly', unit: '$', definition: aggregate('cash_on_hand', 'last') },
  { key: 'revenue_ttm', title: 'Revenue TTM', cadence: 'monthly', unit: '$', definition: aggregate('revenue', 'sum', 12) },
  { key: 'ebitda_ttm', title: 'EBITDA TTM', cadence: 'monthly', unit: '$', definition: aggregate('ebitda', 'sum', 12) },
  { key: 'net_income_ttm', title: 'Net income TTM', cadence: 'monthly', unit: '$', definition: aggregate('net_income', 'sum', 12) },
  { key: 'revenue_quarterly', title: 'Revenue', cadence: 'quarterly', unit: '$', definition: aggregate('revenue', 'sum') },
  { key: 'gross_profit_quarterly', title: 'Gross profit', cadence: 'quarterly', unit: '$', definition: aggregate('gross_profit', 'sum') },
  { key: 'ebitda_quarterly', title: 'EBITDA', cadence: 'quarterly', unit: '$', definition: aggregate('ebitda', 'sum') },
  { key: 'net_income_quarterly', title: 'Net income', cadence: 'quarterly', unit: '$', definition: aggregate('net_income', 'sum') },
  { key: 'gross_margin_quarterly', title: 'Gross margin', cadence: 'quarterly', unit: '%', definition: formula('{gross_profit_quarterly} / {revenue_quarterly}') },
  { key: 'ebitda_margin_quarterly', title: 'EBITDA margin', cadence: 'quarterly', unit: '%', definition: formula('{ebitda_quarterly} / {revenue_quarterly}') },
  { key: 'revenue_per_fte_quarterly', title: 'Revenue per FTE (quarter)', cadence: 'quarterly', unit: '$', definition: formula('SUM({revenue}) / MEAN({headcount})') },
  { key: 'revenue_yearly', title: 'Revenue', cadence: 'yearly', unit: '$', definition: aggregate('revenue', 'sum') },
  { key: 'ebitda_yearly', title: 'EBITDA', cadence: 'yearly', unit: '$', definition: aggregate('ebitda', 'sum') },
];

const subscription: Omit<CatalogEntry, 'group'>[] = [
  { key: 'new_mrr', title: 'New MRR', cadence: 'monthly', unit: '$', definition: input },
  { key: 'expansion_mrr', title: 'Expansion MRR', cadence: 'monthly', unit: '$', definition: input },
  { key: 'contraction_mrr', title: 'Contraction MRR', cadence: 'monthly', unit: '$', definition: input },
  { key: 'churned_mrr', title: 'Churned MRR', cadence: 'monthly', unit: '$', definition: input },
  { key: 'new_customers', title: 'New customers', cadence: 'monthly', definition: input },
  { key: 'lost_customers', title: 'Lost customers', cadence: 'monthly', definition: input },
  { key: 'sales_marketing_spend', title: 'Sales & marketing spend', cadence: 'monthly', unit: '$', definition: input },
  { key: 'mrr', title: 'MRR', cadence: 'monthly', unit: '$', definition: formula('PREV({mrr}) + {new_mrr} + {expansion_mrr} - {contraction_mrr} - {churned_mrr}') },
  { key: 'customers', title: 'Customers', cadence: 'monthly', definition: formula('PREV({customers}) + {new_customers} - {lost_customers}') },
  { key: 'arr', title: 'ARR', cadence: 'monthly', unit: '$', definition: formula('{mrr} * 12') },
  { key: 'net_new_mrr', title: 'Net new MRR', cadence: 'monthly', unit: '$', definition: formula('{new_mrr} + {expansion_mrr} - {contraction_mrr} - {churned_mrr}') },
  { key: 'mrr_growth', title: 'MRR growth', cadence: 'monthly', unit: '%', definition: formula('{net_new_mrr} / PREV({mrr})') },
  { key: 'retained_mrr', title: 'Retained MRR', cadence: 'monthly', unit: '$', definition: formula('PREV({mrr}) - {contraction_mrr} - {churned_mrr}') },
  { key: 'gross_revenue_retention', title: 'Gross revenue retention', cadence: 'monthly', unit: '%', definition: formula('{retained_mrr} / PREV({mrr})') },
  { key: 'net_revenue_retention', title: 'Net revenue retention', cadence: 'monthly', unit: '%', definition: formula('({retained_mrr} + {expansion_mrr}) / PREV({mrr})') },
  { key: 'revenue_churn', title: 'Revenue churn', cadence: 'monthly', unit: '%', definition: formula('({contraction_mrr} + {churned_mrr}) / PREV({mrr})') },
  { key: 'quick_ratio', title: 'Quick ratio', cadence: 'monthly', decimals: 2, definition: formula('({new_mrr} + {expansion_mrr}) / ({contraction_mrr} + {churned_mrr})') },
  { key: 'arpa', title: 'ARPA', cadence: 'monthly', unit: '$', definition: formula('{mrr} / {customers}') },
  { key: 'customer_churn', title: 'Customer churn', cadence: 'monthly', unit: '%', definition: formula('{lost_customers} / PREV({customers})') },
  { key: 'customer_retention', title: 'Customer retention', cadence: 'monthly', unit: '%', definition: formula('1 - {customer_churn}') },
  { key: 'average_customer_lifetime', title: 'Average customer lifetime (months)', cadence: 'monthly', decimals: 1, definition: formula('1 / {customer_churn}') },
  { key: 'ltv', title: 'LTV', cadence: 'monthly', unit: '$', definition: formula('{arpa} * {gross_margin} * {average_customer_lifetime}') },
  { key: 'cac', title: 'CAC', cadence: 'monthly', unit: '$', definition: formula('{sales_marketing_spend} / {new_customers}') },
  { key: 'ltv_to_cac', title: 'LTV:CAC', cadence: 'monthly', decimals: 2, definition: formula('{ltv} / {cac}') },
  { key: 'cac_payback', title: 'CAC payback (months)', cadence: 'monthly', decimals: 1, definition: formula('{cac} / ({arpa} * {gross_margin})') },
  { key: 'sales_efficiency', title: 'Sales efficiency', cadence: 'monthly', decimals: 2, definition: formula('({new_mrr} + {expansion_mrr}) * 12 / PREV({sales_marketing_spend})') },
  { key: 'mrr_quarter_end', title: 'MRR (quarter-end)', cadence: 'quarterly', unit: '$', definition: aggregate('mrr', 'last') },
];

const oneTimeSales: Omit<CatalogEntry, 'group'>[] = [
  { key: 'gross_sales', title: 'Gross sales', cadence: 'weekly', unit: '$', definition: input },
  { key: 'orders', title: 'Orders', cadence: 'weekly', definition: input },
  { key: 'visitors', title: 'Visitors', cadence: 'weekly', definition: input },
  { key: 'new_customers_weekly', title: 'New customers', cadence: 'weekly', definition: input },
  { key: 'marketing_spend', title: 'Marketing spend', cadence: 'weekly', unit: '$', definition: input },
  { key: 'average_order_value', title: 'Average order value', cadence: 'weekly', unit: '$', definition: formula('{gross_sales} / {orders}') },
  { key: 'conversion_rate', title: 'Conversion rate', cadence: 'weekly', unit: '%', definition: formula('{orders} / {visitors}') },
  { key: 'sales_per_visitor', title: 'Sales per visitor', cadence: 'weekly', unit: '$', definition: formula('{gross_sales} / {visitors}') },
  { key: 'cac_weekly', title: 'CAC', cadence: 'weekly', unit: '$', definition: formula('{marketing_spend} / {new_customers_weekly}') },
  { key: 'roas', title: 'ROAS', cadence: 'weekly', decimals: 2, definition: formula('{gross_sales} / {marketing_spend}') },
  { key: 'repeat_order_share', title: 'Repeat-order share', cadence: 'weekly', unit: '%', definition: formula('({orders} - {new_customers_weekly}) / {orders}') },
  { key: 'gross_sales_monthly', title: 'Gross sales', cadence: 'monthly', unit: '$', definition: aggregate('gross_sales', 'sum') },
  { key: 'orders_monthly', title: 'Orders', cadence: 'monthly', definition: aggregate('orders', 'sum') },
  { key: 'average_order_value_monthly', title: 'Average order value', cadence: 'monthly', unit: '$', definition: formula('{gross_sales_monthly} / {orders_monthly}') },
];

export const METRIC_CATALOG: readonly CatalogEntry[] = [
  ...fundamentals.map((entry) => ({ ...entry, group: 'fundamentals' as const })),
  ...subscription.map((entry) => ({ ...entry, group: 'subscription' as const })),
  ...oneTimeSales.map((entry) => ({ ...entry, group: 'one_time_sales' as const })),
];

const EVERY_BUSINESS = ['cash_on_hand', 'revenue', 'gross_margin', 'ebitda'];

export const STARTER_SETS: Record<BusinessModel, readonly string[]> = {
  subscription: [...EVERY_BUSINESS, 'mrr', 'net_revenue_retention', 'customer_churn', 'cac_payback'],
  one_time_sales: [...EVERY_BUSINESS, 'gross_sales', 'average_order_value', 'conversion_rate', 'roas'],
  services: EVERY_BUSINESS,
};

const BY_KEY = new Map(METRIC_CATALOG.map((entry) => [entry.key, entry]));

export function catalogEntry(key: string | undefined): CatalogEntry | undefined {
  return key ? BY_KEY.get(key) : undefined;
}

export function catalogExpression(entry: CatalogEntry): FormulaExpression | undefined {
  if (entry.definition.origin !== 'formula') return undefined;
  return parseFormulaExpression(entry.definition.formula, BY_KEY, { cadence: entry.cadence, self: entry.key });
}

export function catalogReferences(entry: CatalogEntry): string[] {
  const definition = entry.definition;
  if (definition.origin === 'aggregate') return [definition.source];
  if (definition.origin === 'input') return [];
  const keys = new Set<string>();
  const walk = (node: FormulaExpression) => {
    if (node.type === 'operation') { walk(node.left); walk(node.right); return; }
    if (node.type !== 'constant' && node.metricId !== entry.key) keys.add(node.metricId);
  };
  walk(catalogExpression(entry)!);
  return [...keys];
}

export function catalogIsRunningTotal(entry: CatalogEntry): boolean {
  if (entry.definition.origin !== 'formula') return false;
  const walk = (node: FormulaExpression): boolean => node.type === 'operation'
    ? walk(node.left) || walk(node.right)
    : node.type === 'previous' && node.metricId === entry.key;
  return walk(catalogExpression(entry)!);
}

function withMetricIds(expression: FormulaExpression, idOf: (key: string) => string): FormulaExpression {
  if (expression.type === 'constant') return expression;
  if (expression.type === 'operation') {
    return { ...expression, left: withMetricIds(expression.left, idOf), right: withMetricIds(expression.right, idOf) };
  }
  return { ...expression, metricId: idOf(expression.metricId) };
}

export function catalogDefinitionFor(
  entry: CatalogEntry,
  idOf: (key: string) => string,
): Pick<Metric, 'origin' | 'formulaExpression' | 'aggregate'> {
  const definition = entry.definition;
  if (definition.origin === 'input') return { origin: 'input' };
  if (definition.origin === 'aggregate') {
    return {
      origin: 'aggregate',
      aggregate: {
        sourceMetricId: idOf(definition.source),
        function: definition.function,
        trailingPeriods: definition.trailingPeriods,
        incompletePolicy: 'require_complete',
      },
    };
  }
  return { origin: 'formula', formulaExpression: withMetricIds(catalogExpression(entry)!, idOf) };
}

export function keepsCatalogKey(metric: Metric, metrics: Metric[]): boolean {
  const entry = catalogEntry(metric.catalogKey);
  if (!entry) return true;
  if (metric.cadence !== entry.cadence) return false;
  if (metric.origin === 'input') return true;
  const byId = new Map(metrics.map((candidate) => [candidate.id, candidate]));
  const keyOf = (id: string) => (id === metric.id ? entry.key : byId.get(id)?.catalogKey ?? `metric:${id}`);
  const definition = entry.definition;
  if (metric.origin === 'aggregate') {
    return definition.origin === 'aggregate' && !!metric.aggregate
      && keyOf(metric.aggregate.sourceMetricId) === definition.source
      && metric.aggregate.function === definition.function
      && (metric.aggregate.trailingPeriods ?? undefined) === definition.trailingPeriods;
  }
  return definition.origin === 'formula' && !!metric.formulaExpression
    && JSON.stringify(withMetricIds(metric.formulaExpression, keyOf)) === JSON.stringify(catalogExpression(entry));
}

export const catalogTitleOf = (key: string) => BY_KEY.get(key)?.title ?? key;

const PLACEHOLDER = 'catalog:';
export const catalogPlaceholder = (key: string) => PLACEHOLDER + key;
export const placeholderKey = (id: string) => (id.startsWith(PLACEHOLDER) ? id.slice(PLACEHOLDER.length) : undefined);

export function catalogPlaceholderExpression(entry: CatalogEntry): FormulaExpression | undefined {
  const expression = catalogExpression(entry);
  return expression && withMetricIds(expression, catalogPlaceholder);
}

export function placeholderReferences(expression: FormulaExpression, selfKey: string): string[] {
  const keys = new Set<string>();
  const walk = (node: FormulaExpression) => {
    if (node.type === 'operation') { walk(node.left); walk(node.right); return; }
    const key = node.type === 'constant' ? undefined : placeholderKey(node.metricId);
    if (key && key !== selfKey) keys.add(key);
  };
  walk(expression);
  return [...keys];
}

export function resolvePlaceholders(expression: FormulaExpression, idOf: (key: string) => string): FormulaExpression {
  return withMetricIds(expression, (id) => {
    const key = placeholderKey(id);
    return key ? idOf(key) : id;
  });
}

export const byCadenceAndTitle = (a: { cadence: Cadence; title: string }, b: { cadence: Cadence; title: string }) =>
  CADENCE_RANK[a.cadence] - CADENCE_RANK[b.cadence] || a.title.localeCompare(b.title);

export function catalogIngredients(key: string): CatalogEntry[] {
  const ordered: CatalogEntry[] = [];
  const seen = new Set<string>();
  const visit = (current: string) => {
    if (seen.has(current)) return;
    seen.add(current);
    const entry = BY_KEY.get(current);
    if (!entry) return;
    for (const reference of catalogReferences(entry)) visit(reference);
    if (current !== key) ordered.push(entry);
  };
  visit(key);
  return ordered;
}
